import { useCallback, useEffect, useState } from "react";
import { Button, Card, Select, Space, Table, Tag, Tooltip, Typography } from "@arco-design/web-react";
import { ScheduledJobAPI } from "@/apis/system";
import { DateTime } from "@/components/DateTime";
import { IconRefresh } from "@arco-design/web-react/icon";

const { Text } = Typography;

const STATUS_TAG_MAP = {
    SUCCESS: { color: 'green', text: 'Success' },
    FAILED: { color: 'red', text: 'Failed' },
    RUNNING: { color: 'blue', text: 'Running' },
};

const formatDuration = (ms) => {
    if (ms == null) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
};

export const JobLogs = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [jobKey, setJobKey] = useState(undefined);
    const [status, setStatus] = useState(undefined);
    const [jobOptions, setJobOptions] = useState([]);

    const refreshLogs = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, pageSize: 20 };
            if (jobKey) params.jobKey = jobKey;
            if (status) params.status = status;
            const res = await ScheduledJobAPI.getJobLogs(params);
            setLogs(res.records);
            setTotal(res.total);
        } catch (e) {
            console.error('Failed to load job logs', e);
        } finally {
            setLoading(false);
        }
    }, [page, jobKey, status]);

    const loadJobOptions = useCallback(async () => {
        try {
            const jobs = await ScheduledJobAPI.getAllJobs();
            setJobOptions(jobs.map(j => ({
                label: j.jobName,
                value: j.jobKey,
            })));
        } catch (e) {
            console.error('Failed to load job options', e);
        }
    }, []);

    useEffect(() => {
        loadJobOptions();
    }, [loadJobOptions]);

    useEffect(() => {
        refreshLogs();
    }, [refreshLogs]);

    const columns = [
        {
            title: 'Job Name',
            dataIndex: 'jobName',
            width: 220,
            render: (text, record) => (
                <div>
                    <div style={{ fontWeight: 500 }}>{text}</div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-3)' }}>{record.jobKey}</div>
                </div>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            width: 100,
            render: (val) => {
                const info = STATUS_TAG_MAP[val] || {};
                return <Tag color={info.color}>{info.text || val}</Tag>;
            },
        },
        {
            title: 'Trigger',
            dataIndex: 'triggerType',
            width: 100,
            render: (val) => (
                <Tag color={val === 'MANUAL' ? 'orange' : 'purple'} size='small'>
                    {val === 'MANUAL' ? 'Manual' : 'Scheduled'}
                </Tag>
            ),
        },
        {
            title: 'Start Time',
            dataIndex: 'startTime',
            width: 170,
            render: (val) => val ? <DateTime value={val} /> : '-',
        },
        {
            title: 'End Time',
            dataIndex: 'endTime',
            width: 170,
            render: (val) => val ? <DateTime value={val} /> : '-',
        },
        {
            title: 'Duration',
            dataIndex: 'durationMs',
            width: 100,
            render: (val) => formatDuration(val),
        },
        {
            title: 'Error',
            dataIndex: 'errorMessage',
            ellipsis: true,
            render: (val) => val ? (
                <Tooltip content={val}>
                    <Text style={{ color: 'var(--color-danger)' }} ellipsis>{val}</Text>
                </Tooltip>
            ) : '-',
        },
    ];

    return (
        <Card
            title="Job Execution Logs"
            extra={
                <Button icon={<IconRefresh />} onClick={refreshLogs} loading={loading}>
                    Refresh
                </Button>
            }
        >
            <Space style={{ marginBottom: 16 }}>
                <Select
                    style={{ width: 240 }}
                    placeholder="Filter by Job"
                    allowClear
                    showSearch
                    options={jobOptions}
                    value={jobKey}
                    onChange={(val) => { setJobKey(val); setPage(1); }}
                    filterOption={(inputValue, option) =>
                        option.props.children.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                    }
                />
                <Select
                    style={{ width: 140 }}
                    placeholder="Filter by Status"
                    allowClear
                    value={status}
                    onChange={(val) => { setStatus(val); setPage(1); }}
                    options={[
                        { label: 'Success', value: 'SUCCESS' },
                        { label: 'Failed', value: 'FAILED' },
                        { label: 'Running', value: 'RUNNING' },
                    ]}
                />
            </Space>

            <Table
                rowKey="id"
                columns={columns}
                data={logs}
                loading={loading}
                pagination={{ total, current: page, pageSize: 20 }}
                onChange={(pagination) => setPage(pagination.current)}
                scroll={{ x: 1100 }}
                size='small'
            />
        </Card>
    );
};
